import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { toColor, toGlyphName, type Color, type GlyphName } from "@salestrak/ui";

import { VIEW_TYPE_META, viewVisual } from "#app/components/view-options/view-type-meta";
import {
  EntityViewsByTypeDocument,
  type EntityViewRow,
  type ObjectVisibility,
} from "#app/lib/data";
import { viewPath } from "#app/lib/views/view-routes";
import {
  useWorkspaceData,
  type WorkspaceWorkflow,
} from "#app/lib/data/workspace-data-provider";

/**
 * The dashboard surfaces the workspace offers, each carrying its OWN identity
 * — the view's name/icon/color, not the host entity's. A dashboard is a
 * dashboard-type entity view (renameable, recolorable, authored, several per
 * scope) in one of two scopes: a WORKFLOW's tab strip, or the WORKSPACE
 * itself — the dashboards page's own rows, which read from a workflow set
 * and are visible by their own ACL. A team's Dashboard tab is a fixed tab on
 * the team page, like its Overview — team-page chrome, not a view anyone
 * made — so it is not a surface here.
 *
 * This vocabulary is client-only on purpose (the ownership test: "dashboard
 * surface" composes two server concepts — the entity-view row and its host —
 * that no second app could ask the API for as one noun).
 */

export type DashboardScope =
  | {
      kind: "workflow";
      id: string;
      workflow: WorkspaceWorkflow;
    }
  | {
      kind: "workspace";
      /** The workspace id — the row's `scopeId`. */
      id: string;
      /** The source set, resolved to the workflows the viewer can see. An id
       *  the viewer cannot reach drops silently, as its data already does. */
      workflows: WorkspaceWorkflow[];
    };

export type DashboardSurface = {
  scope: DashboardScope;
  /** The stored view row the surface presents — what the row's actions
   *  (rename, duplicate, delete, share) write to. */
  view: EntityViewRow;
  /** The built-in key for seeded dashboards, the view id otherwise — stable
   *  across renames, so it is what recents/favorites store. */
  viewKey: string;
  name: string;
  icon: GlyphName;
  color: Color;
  route: string;
  /** The stored view row's audit fields — null on a surface whose scope was
   *  never seeded (there is no row to have dates or an author yet). Seeded
   *  built-ins carry dates but a null author (the server seeded them). */
  createdAt: string | null;
  updatedAt: string | null;
  ownerId: string | null;
  /** Who can reach it. A workspace dashboard's own row; a workflow
   *  dashboard inherits its workflow's. */
  visibility: ObjectVisibility;
  /** A workspace dashboard with at least one enabled share. Always false on
   *  a workflow dashboard — the workflow's share list is not the view's. */
  isShared: boolean;
};

/**
 * A stored pointer at a dashboard surface plus the identity snapshot rendered
 * before the live data hydrates (the nav-prefs first-paint-mirror pattern).
 * The live surface always wins once it resolves.
 */
export type StoredDashboardRef = {
  kind: "workflow" | "workspace";
  id: string;
  /** Absent on entries stored before dashboards had per-view identity; those
   *  all pointed at the built-in Dashboard tab. */
  viewKey?: string;
  name: string;
  icon?: string;
  color?: string;
};

const DASHBOARD_SEED = VIEW_TYPE_META.dashboard;

/** The built-in dashboard's key: every team's fixed Dashboard tab, and the
 *  seeded `dashboard` view on workflows created while the seed set included
 *  one. New workflows start with List + Board only. */
export const BUILT_IN_DASHBOARD_KEY = "dashboard";

export function dashboardRefKey(ref: {
  kind: string;
  id: string;
  viewKey?: string;
}): string {
  return `${ref.kind}:${ref.id}:${ref.viewKey ?? BUILT_IN_DASHBOARD_KEY}`;
}

/**
 * Ownership is the view row's author. A seeded built-in has none, so it
 * belongs to nobody — the My Dashboards slice and its sidebar count both
 * stay empty until the viewer has actually made a dashboard.
 */
export function surfaceOwnedBy(
  surface: DashboardSurface,
  userId: string | null | undefined,
): boolean {
  return userId != null && surface.ownerId === userId;
}

export function surfaceRefKey(surface: DashboardSurface): string {
  return dashboardRefKey({
    kind: surface.scope.kind,
    id: surface.scope.id,
    viewKey: surface.viewKey,
  });
}

/** The canonical destination a stored ref names, derivable without the live
 *  row: a workspace dashboard is its own page, built-ins keep their tab
 *  segment, custom workflow views are `/view/<id>`. */
export function storedDashboardRoute(ref: StoredDashboardRef): string {
  const viewKey = ref.viewKey ?? BUILT_IN_DASHBOARD_KEY;
  if (ref.kind === "workspace") return `/dashboards/${viewKey}`;
  return viewKey === BUILT_IN_DASHBOARD_KEY
    ? `/workflow/${ref.id}/dashboard`
    : `/workflow/${ref.id}/view/${viewKey}`;
}

/** The identity snapshot a visit/favorite stores for its first paint. */
export function dashboardSnapshot(surface: DashboardSurface): StoredDashboardRef {
  return {
    kind: surface.scope.kind,
    id: surface.scope.id,
    viewKey: surface.viewKey,
    name: surface.name,
    icon: surface.icon,
    color: surface.color,
  };
}

/** A stored snapshot's visual, narrowed back from persisted strings. */
export function storedDashboardVisual(ref: StoredDashboardRef): {
  icon: GlyphName;
  color: Color;
} {
  return {
    icon: toGlyphName(ref.icon) ?? DASHBOARD_SEED.icon,
    color: toColor(ref.color) ?? DASHBOARD_SEED.color,
  };
}

/** A stored ref resolved for display — live identity when the surface is
 *  loaded, the snapshot before that. */
export type ResolvedDashboardRef = {
  refKey: string;
  name: string;
  icon: GlyphName;
  color: Color;
  route: string;
};

/**
 * Resolve a stored ref against the live surfaces. The live surface always
 * wins; before everything is loaded the snapshot paints (absence proves
 * nothing yet); once loaded, a ref with no surface names something the viewer
 * can no longer see and drops.
 *
 * Storage is untyped at run time: an entry whose kind is not a scope this
 * module offers (a team ref, from when team Dashboard tabs were listed) has
 * no route to paint and drops before the loaded check.
 */
export function resolveStoredDashboard(
  stored: StoredDashboardRef,
  surfaceByRefKey: Map<string, DashboardSurface>,
  isLoaded: boolean,
): ResolvedDashboardRef | null {
  if (stored.kind !== "workflow" && stored.kind !== "workspace") return null;
  const refKey = dashboardRefKey(stored);
  const live = surfaceByRefKey.get(refKey);
  if (live) {
    return {
      refKey,
      name: live.name,
      icon: live.icon,
      color: live.color,
      route: live.route,
    };
  }
  if (isLoaded) return null;
  return {
    refKey,
    name: stored.name,
    ...storedDashboardVisual(stored),
    route: storedDashboardRoute(stored),
  };
}

// Hoisted: an inline variables object re-subscribes the query every render.
const DASHBOARD_VIEWS_VARIABLES = { type: "dashboard" };

export type UseDashboardSurfacesResult = {
  /** Every reachable dashboard, sorted by its own name (host name breaks
   *  ties — two "Dashboard"s must not shuffle between renders). */
  surfaces: DashboardSurface[];
  surfaceByRefKey: Map<string, DashboardSurface>;
  /** True once both the workspace maps and the stored-view read have
   *  answered — before that, absence proves nothing and stored refs render
   *  from their snapshots. */
  isLoaded: boolean;
};

/** The name of what a dashboard reads from — its workflow, or its workflow
 *  set joined — for search and for the name sort's tie-break. */
export function surfaceHostName(surface: DashboardSurface): string {
  const { scope } = surface;
  return scope.kind === "workflow"
    ? (scope.workflow.name ?? "")
    : scope.workflows.map((workflow) => workflow.name ?? "").join(", ");
}

export function useDashboardSurfaces(): UseDashboardSurfacesResult {
  const { workflows, workflowMap, isHydrated } = useWorkspaceData();
  const { data } = useQuery(EntityViewsByTypeDocument, {
    variables: DASHBOARD_VIEWS_VARIABLES,
  });

  return useMemo(() => {
    const viewsByScope = new Map<
      string,
      NonNullable<typeof data>["entityViewsByType"]
    >();
    for (const view of data?.entityViewsByType ?? []) {
      const key = `${view.scopeKind}:${view.scopeId}`;
      const bucket = viewsByScope.get(key);
      if (bucket) bucket.push(view);
      else viewsByScope.set(key, [view]);
    }

    const surfaces: DashboardSurface[] = [];

    // An archived workflow's views are parked, so its dashboards are not
    // destinations to offer.
    for (const workflow of workflows) {
      if (workflow.archived) continue;
      const scope: DashboardScope = {
        kind: "workflow",
        id: workflow.workflowId,
        workflow,
      };
      // A workflow has only the dashboards someone made (or the built-in
      // one workflows seeded before the seed set shrank to List + Board
      // still hold); a never-seeded scope offers none.
      for (const view of viewsByScope.get(`workflow:${workflow.workflowId}`) ?? []) {
        surfaces.push({
          scope,
          view,
          viewKey: view.builtInKey ?? view.id,
          name: view.name,
          ...viewVisual(view),
          route: viewPath({ kind: "workflow", id: workflow.workflowId }, view),
          createdAt: view.creationDate,
          updatedAt: view.lastUpdateDate,
          ownerId: view.createdBy ?? null,
          visibility: workflow.visibility,
          isShared: false,
        });
      }
    }

    // The workspace's own dashboards: the API already scoped the list to the
    // rows this viewer can reach (creator, share, or public).
    for (const [key, views] of viewsByScope) {
      if (!key.startsWith("workspace:")) continue;
      for (const view of views) {
        const scope: DashboardScope = {
          kind: "workspace",
          id: view.scopeId,
          workflows: view.workflowIds.flatMap((id) => {
            const workflow = workflowMap.get(id);
            return workflow ? [workflow] : [];
          }),
        };
        surfaces.push({
          scope,
          view,
          viewKey: view.id,
          name: view.name,
          ...viewVisual(view),
          route: viewPath({ kind: "workspace", id: view.scopeId }, view),
          createdAt: view.creationDate,
          updatedAt: view.lastUpdateDate,
          ownerId: view.createdBy ?? null,
          visibility: view.visibility,
          isShared: view.isShared,
        });
      }
    }

    surfaces.sort(
      (a, b) =>
        a.name.localeCompare(b.name) ||
        surfaceHostName(a).localeCompare(surfaceHostName(b)),
    );

    const surfaceByRefKey = new Map(
      surfaces.map((surface) => [surfaceRefKey(surface), surface]),
    );

    return {
      surfaces,
      surfaceByRefKey,
      isLoaded: isHydrated && data !== undefined,
    };
  }, [workflows, workflowMap, isHydrated, data]);
}
